import React from 'react';
import { useTheme } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import Avatar from '@material-ui/core/Avatar';
import Grid from '@material-ui/core/Grid';

const pillars = [
  {
    icon: (
      <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    ),
    title: 'Rotativa: presencia diaria',
    subtitle: 'Micros en la señal 24/7 de TNO Network. Berlud acompaña la rutina de la audiencia con mensajes de cuidado visual y servicios.',
  },
  {
    icon: (
      <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
      </svg>
    ),
    title: 'TNO Digest: autoridad en Google',
    subtitle: 'Artículos SEO sobre salud visual, lentes, exámenes y estilo. Contenido que permanece y genera tráfico orgánico mes a mes.',
  },
  {
    icon: (
      <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
    title: 'Noticiero + redes',
    subtitle: 'Noticias TNO con Sol Vargas para credibilidad informativa; inserts en clips y publicación en redes oficiales TNO para alcance social.',
  },
];

const AboutSection = () => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });

  return (
    <Box>
      <Box marginBottom={{ xs: 4, sm: 8, md: 10 }}>
        <Typography
          sx={{ textTransform: 'uppercase', fontWeight: 600 }}
          gutterBottom
          color="primary"
          align="center"
        >
          About
        </Typography>
        <Typography variant="h4" fontWeight={700} align="center" gutterBottom>
          El ecosistema que tu óptica merece
        </Typography>
        <Typography variant="h6" component="p" color="textSecondary" align="center" maxWidth={720} margin="0 auto">
          12 años transformando la comunicación digital. TNO Network posiciona a Óptica Berlud con el mismo rigor
          que aplicamos a marcas líderes: noticiero, rotativa, editorial y redes en un solo plan mensual.
        </Typography>
      </Box>
      <Grid container spacing={4} direction={isMd ? 'row' : 'column-reverse'}>
        <Grid item xs={12} md={6} data-aos={isMd ? 'fade-right' : 'fade-up'}>
          <Typography
            sx={{ textTransform: 'uppercase', fontWeight: 600 }}
            gutterBottom
            color="primary"
          >
            Pilares de la propuesta
          </Typography>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Noticiero, rotativa, Digest e inserts
          </Typography>
          <List disablePadding>
            {pillars.map((item, index) => (
              <ListItem key={index} disableGutters sx={{ alignItems: 'flex-start' }} data-aos="fade-up">
                <ListItemAvatar>
                  <Box
                    component={Avatar}
                    variant="rounded"
                    bgcolor={`${theme.palette.primary.light}33`}
                    color={theme.palette.primary.dark}
                  >
                    {item.icon}
                  </Box>
                </ListItemAvatar>
                <ListItemText
                  primary={item.title}
                  secondary={item.subtitle}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
            ))}
          </List>
        </Grid>
        <Grid item xs={12} md={6} container justifyContent="center" alignItems="center" data-aos={isMd ? 'fade-left' : 'fade-up'}>
          <Box
            component={Card}
            boxShadow={4}
            borderRadius={2}
            overflow="hidden"
            sx={{ bgcolor: theme.palette.alternate.main }}
          >
            <Box p={3}>
              <Typography variant="overline" color="primary" fontWeight={700}>
                Vocera del noticiero
              </Typography>
              <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 0.5 }}>
                Noticias TNO · TNO Network
              </Typography>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Sol Vargas
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Conductora de Noticias TNO. Su voz otorga credibilidad periodística a cada mención y cintillo de salud visual
                de Óptica Berlud: el mensaje llega en contexto informativo, no como publicidad intrusiva.
              </Typography>
              <Typography variant="body2" fontWeight={700} color="primary">
                Prestigio periodístico · Audiencia de actualidad y bienestar
              </Typography>
              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                  Aliado comercial
                </Typography>
                <Typography variant="h6" fontWeight={700} color="primary">
                  Óptica Berlud
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Tradición, calidad y servicio. La propuesta integra noticiero, rotativa 24/7, TNO Digest, inserts y micros
                  en video (Plata y Oro) producidos en estudio TNO o con material de la marca.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AboutSection;
